import { useState, useMemo, useEffect } from 'react'
import { apiLogger } from '../../utils/APIUsageLogger'
import { exchangeRateCache } from '../../utils/ExchangeRateCache'
import { useAuth } from '../../hooks/useAuth'
import { TrendingUp, Zap, DollarSign, Clock, TrendingDown } from 'lucide-react'

export function ApiUsageDashboard() {
  const [period, setPeriod] = useState('day')
  const [exchangeRate, setExchangeRate] = useState(null)
  const [, setLoading] = useState(true)
  const { user } = useAuth()
  const isTestUser = user?.email === 'test@sfpwealth.co.za'

  // Fetch exchange rate on mount
  useEffect(() => {
    const loadRate = async () => {
      setLoading(true)
      const rate = await exchangeRateCache.getRate()
      setExchangeRate(rate)
      setLoading(false)
    }
    loadRate()
  }, [])

  const stats = useMemo(() => {
    const logs = apiLogger.getAllLogs()
    if (logs.length === 0) {
      return {
        totalCalls: 0,
        totalInputTokens: 0,
        totalOutputTokens: 0,
        totalCost: 0,
        successCount: 0,
        errorCount: 0,
        byModel: {},
      }
    }

    // Filter logs by period
    const now = new Date()
    const cutoffDate = new Date()

    if (period === 'day') {
      cutoffDate.setDate(cutoffDate.getDate() - 1)
    } else if (period === 'week') {
      cutoffDate.setDate(cutoffDate.getDate() - 7)
    } else if (period === 'month') {
      cutoffDate.setMonth(cutoffDate.getMonth() - 1)
    }

    const filteredLogs = logs.filter(log => {
      const logDate = log.timestamp ? new Date(log.timestamp) : new Date()
      return logDate >= cutoffDate && logDate <= now
    })

    // Calculate stats
    const stats = {
      totalCalls: filteredLogs.length,
      totalInputTokens: 0,
      totalOutputTokens: 0,
      totalCost: 0,
      successCount: 0,
      errorCount: 0,
      byModel: {},
    }

    filteredLogs.forEach(log => {
      stats.totalInputTokens += log.inputTokens || 0
      stats.totalOutputTokens += log.outputTokens || 0
      stats.totalCost += log.totalCost || apiLogger.getCostEstimate(log.model, log.inputTokens || 0, log.outputTokens || 0)

      if (log.status === 'success') {
        stats.successCount += 1
      } else {
        stats.errorCount += 1
      }

      if (!stats.byModel[log.model]) {
        stats.byModel[log.model] = { calls: 0, cost: 0 }
      }
      stats.byModel[log.model].calls += 1
      stats.byModel[log.model].cost += log.totalCost || apiLogger.getCostEstimate(log.model, log.inputTokens || 0, log.outputTokens || 0)
    })

    return stats
  }, [period])

  const formatTokens = (n) => {
    if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(2)}M`
    if (n >= 1_000) return `${(n / 1_000).toFixed(1)}k`
    return n.toString()
  }

  const formatCost = (cost) => {
    if (cost === 0) return '$0.00'
    if (cost < 0.001) return `$${cost.toFixed(6)}`
    return `$${cost.toFixed(4)}`
  }

  const formatRand = (cost) => {
    if (cost === 0) return 'R0.00'
    if (cost < 0.01) return `R${cost.toFixed(4)}`
    return `R${cost.toFixed(2)}`
  }

  const convertToRand = (usdAmount) => {
    if (!exchangeRate) return 0
    return usdAmount * exchangeRate
  }

  const addMarkup = (randAmount) => {
    return randAmount * 1.4 // 40% markup
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Zap className="w-6 h-6 text-sfp-orange" />
          <h2 className="text-2xl font-bold text-sfp-darkgray">API Usage</h2>
        </div>

        {/* Period Selector */}
        <div className="flex gap-2">
          {['day', 'week', 'month'].map(p => (
            <button
              key={p}
              onClick={() => setPeriod(p)}
              className={`px-4 py-2 rounded-lg font-medium transition-colors text-sm ${
                period === p
                  ? 'bg-sfp-orange text-white'
                  : 'bg-sfp-lightgray text-sfp-darkgray hover:bg-gray-300'
              }`}
            >
              {p === 'day' ? 'Today' : p === 'week' ? '7 Days' : '30 Days'}
            </button>
          ))}
        </div>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {/* Total Calls */}
        <div className="bg-sfp-white border border-sfp-border rounded-lg p-4 shadow-sm">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-gray-500 text-sm font-medium">Total Calls</p>
              <p className="text-3xl font-bold text-sfp-darkgray">{stats.totalCalls}</p>
            </div>
            <Clock className="w-8 h-8 text-blue-500 opacity-20" />
          </div>
          <p className="text-xs text-gray-400 mt-2">
            {stats.successCount} success, {stats.errorCount} errors
          </p>
        </div>

        {/* Input Tokens */}
        <div className="bg-sfp-white border border-sfp-border rounded-lg p-4 shadow-sm">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-gray-500 text-sm font-medium">Input Tokens</p>
              <p className="text-3xl font-bold text-sfp-darkgray">{formatTokens(stats.totalInputTokens)}</p>
            </div>
            <TrendingUp className="w-8 h-8 text-green-500 opacity-20" />
          </div>
          <p className="text-xs text-gray-400 mt-2">
            {stats.totalInputTokens.toLocaleString()}
          </p>
        </div>

        {/* Output Tokens */}
        <div className="bg-sfp-white border border-sfp-border rounded-lg p-4 shadow-sm">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-gray-500 text-sm font-medium">Output Tokens</p>
              <p className="text-3xl font-bold text-sfp-darkgray">{formatTokens(stats.totalOutputTokens)}</p>
            </div>
            <TrendingUp className="w-8 h-8 text-blue-500 opacity-20" />
          </div>
          <p className="text-xs text-gray-400 mt-2">
            {stats.totalOutputTokens.toLocaleString()}
          </p>
        </div>

        {/* Total Cost */}
        <div className="bg-gradient-to-br from-sfp-orange to-orange-600 rounded-lg p-4 shadow-sm">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-orange-100 text-sm font-medium">Total Cost (USD)</p>
              <p className="text-3xl font-bold text-white">{formatCost(stats.totalCost)}</p>
            </div>
            <DollarSign className="w-8 h-8 text-white opacity-30" />
          </div>
          <p className="text-xs text-orange-100 mt-2">
            {stats.totalCalls > 0 ? formatCost(stats.totalCost / stats.totalCalls) : '$0.00'} avg/call
          </p>
          {exchangeRate && (
            <p className="text-xs text-orange-50 mt-1 opacity-75">
              Rate: 1 USD = R{exchangeRate.toFixed(2)}
            </p>
          )}
        </div>
      </div>

      {/* Rand Conversion & Billing Section */}
      {exchangeRate && (
        <div className="bg-sfp-white border border-sfp-border rounded-lg p-6 shadow-sm">
          <h3 className="text-lg font-bold text-sfp-darkgray mb-4">Rand Conversion & Billing</h3>

          {/* Exchange Rate Info */}
          <div className="mb-4 p-3 bg-sfp-lightgray rounded border border-sfp-border">
            <p className="text-xs text-gray-500 font-medium">DAILY EXCHANGE RATE (Cached for Billing Accuracy)</p>
            <p className="text-2xl font-bold text-sfp-darkgray mt-1">
              1 USD = R{exchangeRate.toFixed(2)}
            </p>
          </div>

          {/* Conversion Breakdown */}
          <div className="space-y-3">
            <div className="flex items-center justify-between pb-3 border-b border-sfp-lightgray">
              <div>
                <p className="font-medium text-sfp-darkgray">Total in USD</p>
                <p className="text-sm text-gray-500">Actual API costs</p>
              </div>
              <p className="text-lg font-bold text-sfp-darkgray">{formatCost(stats.totalCost)}</p>
            </div>

            <div className="flex items-center justify-between pb-3 border-b border-sfp-lightgray">
              <div>
                <p className="font-medium text-sfp-darkgray">Converted to Rand</p>
                <p className="text-sm text-gray-500">At daily rate</p>
              </div>
              <p className="text-lg font-bold text-sfp-darkgray">
                {formatRand(convertToRand(stats.totalCost))}
              </p>
            </div>

            {/* Show markup only for test@sfpwealth.co.za */}
            {isTestUser && (
              <>
                <div className="flex items-center justify-between pb-3 border-b border-sfp-border pt-2">
                  <div>
                    <p className="font-medium text-sfp-darkgray">40% Markup</p>
                    <p className="text-sm text-gray-500">Billing surcharge</p>
                  </div>
                  <p className="text-lg font-bold text-sfp-orange">
                    {formatRand(convertToRand(stats.totalCost) * 0.4)}
                  </p>
                </div>

                <div className="flex items-center justify-between p-3 bg-gradient-to-r from-sfp-orange/10 to-orange-100/10 rounded border border-sfp-orange/20">
                  <div>
                    <p className="font-bold text-sfp-darkgray">Billable Amount (with Markup)</p>
                    <p className="text-xs text-gray-500 mt-1">Total to charge clients</p>
                  </div>
                  <div className="text-right">
                    <p className="text-2xl font-bold text-sfp-orange">
                      {formatRand(addMarkup(convertToRand(stats.totalCost)))}
                    </p>
                  </div>
                </div>
              </>
            )}
          </div>

          {/* Info Message */}
          <div className="mt-4 p-3 bg-blue-50 border border-blue-200 rounded">
            <p className="text-xs text-blue-700">
              <strong>Note:</strong> Exchange rate is cached daily for accurate billing. The same rate is used throughout the day regardless of actual market fluctuations.
              {isTestUser && (
                <span className="block mt-1">
                  Markup is visible because you're logged in as {user?.email}
                </span>
              )}
            </p>
          </div>
        </div>
      )}

      {/* Model Breakdown */}
      {stats.totalCalls > 0 && Object.keys(stats.byModel).length > 0 && (
        <div className="bg-sfp-white border border-sfp-border rounded-lg p-6 shadow-sm">
          <h3 className="text-lg font-bold text-sfp-darkgray mb-4">Breakdown by Model</h3>
          <div className="space-y-3">
            {Object.entries(stats.byModel).map(([model, data]) => (
              <div key={model} className="flex items-center justify-between pb-3 border-b border-sfp-lightgray last:border-b-0">
                <div>
                  <p className="font-medium text-sfp-darkgray">{model}</p>
                  <p className="text-sm text-gray-500">{data.calls} call{data.calls !== 1 ? 's' : ''}</p>
                </div>
                <p className="text-lg font-bold text-sfp-darkgray">{formatCost(data.cost)}</p>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Empty State */}
      {stats.totalCalls === 0 && (
        <div className="bg-sfp-lightgray border border-dashed border-sfp-border rounded-lg p-12 text-center">
          <Zap className="w-12 h-12 text-gray-300 mx-auto mb-4" />
          <p className="text-sfp-darkgray font-medium">No API usage in this period</p>
          <p className="text-gray-500 text-sm mt-1">
            API calls will appear here as you integrate with your claim portal
          </p>
        </div>
      )}

      {/* Clear Logs Button */}
      <div className="flex justify-end">
        <button
          onClick={() => {
            if (confirm('Are you sure you want to clear all API logs? This cannot be undone.')) {
              apiLogger.clearLogs()
              window.location.reload()
            }
          }}
          className="px-4 py-2 text-sm font-medium text-red-600 bg-red-50 border border-red-200 rounded-lg hover:bg-red-100 transition-colors"
        >
          Clear All Logs
        </button>
      </div>
    </div>
  )
}
